import React from "react";
import { Check, X, MapPin, TrendingUp, Target, Users, UserPlus, Eye, BookOpen } from "lucide-react";
import { PLAYER_DESTINATIONS, COACH_DESTINATIONS } from "../kit/destinations";

/**
 * The mini-screen drawings of the guided tour (#1514), ported as static markup
 * from the approved canvas boards (Main, P1 to P5, C0 to C3). Nothing here is
 * interactive: no button, link or field, only text and boxes, so the drawings
 * add no tap target. The ribbon labels come from `kit/destinations.js` (the
 * real ones), not a copy. Colours are the kit's `--nw-*` tokens; the drawn hex
 * values are the kit's. Sizes are fixed px, as drawn.
 */

const eyebrow = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--nw-ink-muted)",
};
const cell = {
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-control)",
  padding: "8px 10px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
const dash = { fontSize: 14, fontWeight: 600, color: "var(--nw-ink-faint)" };
const smallLabel = { fontSize: 10, color: "var(--nw-ink-muted)" };
const placeholder = { color: "var(--nw-ink-faint)", fontStyle: "italic" };

function Drawing({ label, children }) {
  return (
    <div
      role="img"
      aria-label={label}
      data-testid="tour-art"
      style={{
        marginTop: 20,
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function Body({ children }) {
  return <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>{children}</div>;
}

/** The ribbon strip as drawn: the real destination labels, one of them current. */
function RibbonStrip({ destinations, current, gap }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: gap ? undefined : "space-between",
        gap: gap || undefined,
        padding: "10px 14px 0",
        borderBottom: "1px solid var(--nw-line)",
      }}
    >
      {destinations.map((d) => {
        const on = d.key === current;
        return (
          <span
            key={d.key}
            style={{
              fontSize: 11,
              fontWeight: on ? 700 : 500,
              color: on ? "var(--nw-navy)" : "var(--nw-ink-muted)",
              paddingBottom: 8,
              borderBottom: on ? "2px solid var(--nw-navy)" : undefined,
            }}
          >
            {d.label}
          </span>
        );
      })}
    </div>
  );
}

const playerRibbon = (current) => <RibbonStrip destinations={PLAYER_DESTINATIONS} current={current} />;
const coachRibbon = () => <RibbonStrip destinations={COACH_DESTINATIONS} current="players" gap={18} />;

function Cell({ label, children }) {
  return (
    <div style={cell}>
      <span style={smallLabel}>{label}</span>
      {children}
    </div>
  );
}

function DrawnButton({ children, height = 36, fill = true, fontSize = 12 }) {
  return (
    <div
      style={{
        height,
        borderRadius: 8,
        background: fill ? "var(--nw-navy)" : undefined,
        border: fill ? undefined : "1px solid var(--nw-navy)",
        color: fill ? "#FFFFFF" : "var(--nw-navy)",
        fontSize,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  );
}

function RecordArt() {
  return (
    <Drawing label="A small drawing of the Record Match screen">
      {playerRibbon("record")}
      <Body>
        <div style={eyebrow}>Log a match · Scorecard</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
          <Cell label="Runs"><span style={dash}>—</span></Cell>
          <Cell label="Balls"><span style={dash}>—</span></Cell>
          <Cell label="How out"><span style={dash}>—</span></Cell>
          <Cell label="Overs · Wkts · Runs"><span style={dash}>—</span></Cell>
        </div>
        <DrawnButton>Save match</DrawnButton>
      </Body>
    </Drawing>
  );
}

function DashboardArt() {
  // Drawn from the shipped screen (RecordDashboardScreen.jsx): the Career highlights
  // figures and one chart, kept short so the card fits a phone (#1560). Figures are dashes.
  return (
    <Drawing label="A small drawing of the Dashboard screen">
      {playerRibbon("dashboard")}
      <Body>
        <div style={eyebrow}>Your record</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--nw-ink)" }}>Dashboard</div>
        <div style={eyebrow}>Career highlights</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
          <Cell label="Matches"><span style={dash}>—</span></Cell>
          <Cell label="Runs"><span style={dash}>—</span></Cell>
          <Cell label="Average"><span style={dash}>—</span></Cell>
        </div>
        <div style={eyebrow}>Cumulative runs</div>
        <div style={{ height: 36, borderRadius: "var(--nw-radius-control)", background: "var(--nw-line-faint)", border: "1px solid var(--nw-line)" }} />
      </Body>
    </Drawing>
  );
}

function PlanArt() {
  return (
    <Drawing label="A small drawing of the Plan screen">
      {playerRibbon("plan")}
      <Body>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--nw-ink)" }}>Your plan</div>
        <div style={eyebrow}>What you told us</div>
        <div
          style={{
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            padding: "10px 12px",
            fontSize: 13,
            lineHeight: 1.4,
            ...placeholder,
          }}
        >
          [THE SENTENCE FROM SIGN-UP]
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ ...cell, flex: 1 }}><span style={smallLabel}>Goal</span><span style={{ ...dash, fontSize: 12 }}>—</span></div>
          <div style={{ ...cell, flex: 1 }}><span style={smallLabel}>Target date</span><span style={{ ...dash, fontSize: 12 }}>—</span></div>
        </div>
        <DrawnButton>Make it a plan</DrawnButton>
      </Body>
    </Drawing>
  );
}

function TrainingArt() {
  const chip = { width: 28, height: 28, borderRadius: 6, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" };
  return (
    <Drawing label="A small drawing of the Training and guides screen">
      {playerRibbon("learn")}
      <Body>
        <div style={eyebrow}>My training · Capture session</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--nw-ink-lede)" }}>Nets, how did it go?</span>
          <div style={{ display: "flex", gap: 6 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <span
                key={n}
                style={
                  n === 4
                    ? { ...chip, background: "var(--nw-navy)", fontWeight: 700, color: "#FFFFFF" }
                    : { ...chip, border: "1px solid var(--nw-line-strong)", color: "var(--nw-ink-lede)" }
                }
              >
                {n}
              </span>
            ))}
          </div>
        </div>
        <div style={{ height: 1, background: "var(--nw-line)" }} />
        <div style={eyebrow}>Assigned to you</div>
        <div
          style={{
            border: "1px dashed var(--nw-line-strong)",
            borderRadius: "var(--nw-radius-control)",
            padding: "10px 12px",
            fontSize: 12,
            color: "var(--nw-ink-muted)",
          }}
        >
          Nothing assigned yet. Drills and guides your coach sends land here.
        </div>
      </Body>
    </Drawing>
  );
}

function GroundsArt() {
  return (
    <Drawing label="A small drawing of the Grounds screen">
      {playerRibbon("grounds")}
      <Body>
        <div
          style={{
            height: 96,
            borderRadius: "var(--nw-radius-control)",
            background: "var(--nw-line-faint)",
            border: "1px solid var(--nw-line)",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MapPin size={28} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" />
          <span style={{ position: "absolute", left: 10, bottom: 8, fontSize: 10, color: "var(--nw-ink-muted)" }}>Map · grounds near you</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--nw-ink)" }}>Nearby</span>
          <span style={{ fontSize: 11, color: "var(--nw-ink-muted)" }}>Played · Saved</span>
        </div>
        <div
          style={{
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            padding: "8px 10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, ...placeholder }}>[A GROUND NEAR YOU]</span>
          <span style={{ fontSize: 11, color: "var(--nw-ink-muted)" }}>Pitch · facilities</span>
        </div>
        <DrawnButton height={32} fill={false}>Can't find yours? Add a ground</DrawnButton>
      </Body>
    </Drawing>
  );
}

function CoachAccessArt() {
  const row = { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--nw-ink-lede)" };
  return (
    <Drawing label="A small drawing of the coach access card in Settings">
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--nw-line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--nw-ink)" }}>Settings</span>
        <span style={{ fontSize: 11, color: "var(--nw-ink-muted)" }}>Your coach</span>
      </div>
      <Body>
        <div style={{ fontSize: 12, lineHeight: 1.45, color: "var(--nw-ink-lede)" }}>
          No coach linked. Invite one by email, or accept an invite a coach sends you.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={row}><Check size={14} color="var(--nw-ok)" strokeWidth={2.5} aria-hidden="true" />They can read your matches, plan, training and reflections</div>
          <div style={row}><Check size={14} color="var(--nw-ok)" strokeWidth={2.5} aria-hidden="true" />They can assign drills and guides, and leave you feedback</div>
          <div style={row}><X size={14} color="var(--nw-alert)" strokeWidth={2.5} aria-hidden="true" />They cannot see your monthly check-ins or change your account</div>
        </div>
        <DrawnButton height={32} fill={false}>Invite a coach</DrawnButton>
      </Body>
    </Drawing>
  );
}

function CoachInviteArt() {
  return (
    <Drawing label="A small drawing of the empty coach portal">
      {coachRibbon()}
      <Body>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--nw-ink)" }}>Your players</div>
        <div style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>No players yet.</div>
        <div
          style={{
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span style={smallLabel}>Player's email</span>
          <span style={{ fontSize: 12, ...placeholder }}>[PLAYER'S EMAIL]</span>
        </div>
        <DrawnButton>Invite a player</DrawnButton>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--nw-ink-muted)" }}>
          <span
            style={{
              display: "inline-flex",
              padding: "2px 8px",
              borderRadius: "var(--nw-radius-pill)",
              background: "var(--nw-line-faint)",
              border: "1px solid var(--nw-line)",
              color: "var(--nw-ink-lede)",
              fontWeight: 600,
            }}
          >
            Invited
          </span>
          <span>until the player accepts</span>
        </div>
      </Body>
    </Drawing>
  );
}

function CoachPlayerArt() {
  const tile = { ...cell, gap: 0, fontSize: 11, fontWeight: 600, color: "var(--nw-ink)" };
  const ghost = { flex: 1 };
  return (
    <Drawing label="A small drawing of a player's page in the coach portal">
      {coachRibbon()}
      <Body>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 28, height: 28, borderRadius: "var(--nw-radius-pill)", background: "var(--nw-navy-tint)", border: "1px solid var(--nw-line-strong)" }} />
          <span style={{ fontSize: 13, fontWeight: 700, ...placeholder }}>[PLAYER'S NAME]</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
          <div style={tile}>Record</div>
          <div style={tile}>Goals</div>
          <div style={tile}>Training</div>
          <div style={tile}>Reflections</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={ghost}><DrawnButton height={32} fill={false} fontSize={11}>Assign a drill</DrawnButton></div>
          <div style={ghost}><DrawnButton height={32} fill={false} fontSize={11}>Give feedback</DrawnButton></div>
        </div>
        <div
          style={{
            border: "1px dashed var(--nw-line-strong)",
            borderRadius: "var(--nw-radius-control)",
            padding: "8px 10px",
            fontSize: 11,
            color: "var(--nw-ink-muted)",
          }}
        >
          Private note · only you can see this
        </div>
      </Body>
    </Drawing>
  );
}

function CoachGamesArt() {
  const slot = (on) => (
    <span
      style={{
        height: 26,
        borderRadius: "var(--nw-radius-check)",
        background: on ? "var(--nw-navy-tint)" : undefined,
        border: on ? "1px solid var(--nw-navy)" : "1px dashed var(--nw-line-strong)",
      }}
    />
  );
  return (
    <Drawing label="A small drawing of the coach's games and team selection">
      {coachRibbon()}
      <Body>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--nw-ink)" }}>Your games</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--nw-navy)" }}>Schedule a game</span>
        </div>
        <div
          style={{
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--nw-ink-muted)" }}>
            <span>Where will you play?</span>
            <span>When</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 600, ...placeholder }}>
            <span>[GROUND]</span>
            <span>[DATE]</span>
          </div>
        </div>
        <div style={eyebrow}>Team selection</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6 }}>
          {[true, true, true, false, false, false, false, false].map((on, i) => (
            <React.Fragment key={i}>{slot(on)}</React.Fragment>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "var(--nw-ink-muted)" }}>
          Pick from your players. A planned game shows on each picked player's Coming up.
        </div>
      </Body>
    </Drawing>
  );
}

const ART = {
  record: RecordArt,
  dashboard: DashboardArt,
  plan: PlanArt,
  training: TrainingArt,
  grounds: GroundsArt,
  "coach-access": CoachAccessArt,
  "coach-invite": CoachInviteArt,
  "coach-player": CoachPlayerArt,
  "coach-games": CoachGamesArt,
};

export function TourArt({ art }) {
  const Art = ART[art];
  return Art ? <Art /> : null;
}

const ROW_ICONS = {
  "trending-up": TrendingUp,
  target: Target,
  users: Users,
  "user-plus": UserPlus,
  eye: Eye,
  "book-open": BookOpen,
};

/** The welcome card's three rows: an icon tile, a heading, one line. */
export function TourRows({ rows }) {
  return (
    <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 12 }}>
      {rows.map((r) => {
        const Icon = ROW_ICONS[r.icon];
        return (
          <div
            key={r.title}
            data-testid="tour-row"
            style={{
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-card)",
              padding: 14,
            }}
          >
            <div
              style={{
                flex: "0 0 44px",
                height: 44,
                borderRadius: "var(--nw-radius-control)",
                background: "var(--nw-navy-tint)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon size={22} color="var(--nw-navy)" strokeWidth={2} aria-hidden="true" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "var(--nw-ink)" }}>{r.title}</div>
              <div style={{ fontSize: 15, lineHeight: 1.45, color: "var(--nw-ink-lede)" }}>{r.body}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
